import React, { useState, useMemo, useEffect } from "react";
import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import { MButton } from "@/components/ui/atoms/MButtons";
import {
  WheelPicker,
  WheelPickerWrapper,
  type WheelPickerOption,
} from "@/components/ui/atoms/MWheelPicker";
import { cn } from "@/utils";
import styles from "./MDatePicker.module.css";

// Types
export interface MDatePickerProps {
  /**
   * If true, only show year picker (for year-only selection)
   */
  showYearOnly?: boolean;
  open: boolean;
  onClose: () => void;
  onDone: (date: Date) => void;
  initialValue?: Date;
  title?: string;
  minYear?: number;
  maxYear?: number;
  minDate?: Date;
  maxDate?: Date;
  hideDate?: boolean;
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

export const MDatePicker: React.FC<MDatePickerProps> = (props) => {
  const {
    open,
    onClose,
    onDone,
    initialValue,
    title = "Select Date",
    minYear = 1900,
    maxYear,
    minDate,
    maxDate,
    hideDate = false,
    showYearOnly = false,
  } = props;

  // Detect if maxYear was explicitly passed by the user
  const hasExplicitMaxYear = Object.prototype.hasOwnProperty.call(
    props,
    "maxYear"
  );
  // Initial value - use latest allowed date if no initialValue
  const today = new Date();
  const defaultDate = initialValue ? initialValue : today;
  const [selectedYear, setSelectedYear] = useState(defaultDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(defaultDate.getMonth());
  const [selectedDay, setSelectedDay] = useState(
    hideDate ? 1 : defaultDate.getDate()
  );

  // Sync selected values with initialValue or default to latest allowed date
  useEffect(() => {
    if (open) {
      const base = initialValue ? initialValue : today;
      setSelectedYear(base.getFullYear());
      setSelectedMonth(base.getMonth());
      setSelectedDay(hideDate ? 1 : base.getDate());
    }
  }, [open, initialValue, hideDate]);

  // Normalize minDate/maxDate to ensure consistent comparison
  function normalizeDate(date?: Date) {
    if (!date) return undefined;
    // Create a new date object to avoid mutating the input date
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
  }
  const computedMinDate = useMemo(
    () => normalizeDate(minDate) || new Date(minYear, 0, 1),
    [minDate, minYear]
  );
  const computedMaxDate = useMemo(() => {
    if (maxDate) return normalizeDate(maxDate);
    if (!hasExplicitMaxYear) {
      const today = new Date();
      return new Date(today.getFullYear(), today.getMonth(), today.getDate());
    }
    return new Date(maxYear!, 11, 31);
  }, [maxDate, maxYear, hasExplicitMaxYear]);

  // Memoized year, month, days (years are strictly within computedMinDate...computedMaxDate)
  const years = useMemo(() => {
    const arr = [];
    // Fix: ensure years are inclusive and in ascending order for the WheelPicker
    for (
      let y = computedMinDate.getFullYear();
      y <= computedMaxDate.getFullYear();
      y++
    )
      arr.push(y);
    return arr;
  }, [computedMinDate, computedMaxDate]);
  const days = useMemo(
    () =>
      Array.from(
        { length: getDaysInMonth(selectedYear, selectedMonth) },
        (_, i) => i + 1
      ),
    [selectedYear, selectedMonth]
  );

  // Handle selection - only restrict year/month/day to valid ranges
  const handleMonthChange = (idx: number) => {
    // Check if the current selected day would be valid in the new month
    const daysInNewMonth = getDaysInMonth(selectedYear, idx);
    let newSelectedDay = selectedDay;

    // First ensure the day is valid for the month (not exceeding days in month)
    if (newSelectedDay > daysInNewMonth) {
      newSelectedDay = daysInNewMonth;
    }

    // Then check if the new date would be below minDate
    const newDate = new Date(selectedYear, idx, newSelectedDay);
    if (computedMinDate && newDate < computedMinDate) {
      // If below minDate, set to the minimum day allowed in this month
      if (
        selectedYear === computedMinDate.getFullYear() &&
        idx === computedMinDate.getMonth()
      ) {
        newSelectedDay = computedMinDate.getDate();
      }
    }

    // Also check if the new date would be above maxDate
    if (computedMaxDate && newDate > computedMaxDate) {
      // If above maxDate, set to the maximum day allowed in this month
      if (
        selectedYear === computedMaxDate.getFullYear() &&
        idx === computedMaxDate.getMonth()
      ) {
        newSelectedDay = computedMaxDate.getDate();
      }
    }

    setSelectedMonth(idx);
    if (newSelectedDay !== selectedDay) {
      setSelectedDay(newSelectedDay);
    }
  };
  const handleDayChange = (dayIdx: number) => {
    setSelectedDay(dayIdx + 1);
  };
  const handleYearChange = (yearIdx: number) => {
    const newYear = years[yearIdx];

    // Check if the current selected day would be valid in the new year
    const daysInMonth = getDaysInMonth(newYear, selectedMonth);
    let newSelectedDay = selectedDay;

    // First ensure the day is valid for the month in the new year
    if (newSelectedDay > daysInMonth) {
      newSelectedDay = daysInMonth;
    }

    // Then check if the new date would be below minDate
    const newDate = new Date(newYear, selectedMonth, newSelectedDay);
    if (computedMinDate && newDate < computedMinDate) {
      // If below minDate, set to the minimum day allowed
      if (
        newYear === computedMinDate.getFullYear() &&
        selectedMonth === computedMinDate.getMonth()
      ) {
        newSelectedDay = computedMinDate.getDate();
      }
    }

    // Also check if the new date would be above maxDate
    if (computedMaxDate && newDate > computedMaxDate) {
      // If above maxDate, set to the maximum day allowed
      if (
        newYear === computedMaxDate.getFullYear() &&
        selectedMonth === computedMaxDate.getMonth()
      ) {
        newSelectedDay = computedMaxDate.getDate();
      }
    }

    setSelectedYear(newYear);
    if (newSelectedDay !== selectedDay) {
      setSelectedDay(newSelectedDay);
    }

    // --- Fix: Scroll year to center after selection in year-only mode ---
    setTimeout(() => {
      if (showYearOnly) {
        const wheelLists = document.querySelectorAll(`.${styles.wheelList}`);
        const yearWheel = wheelLists[0];
        if (yearWheel) {
          const selectedIndex = years.indexOf(newYear);
          const selectedItem = yearWheel.children[selectedIndex] as HTMLElement;
          if (selectedItem) {
            selectedItem.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }
      }
    }, 60);
  };

  const handleDone = () => {
    const finalDay = hideDate ? 1 : selectedDay;
    const finalDate = new Date(selectedYear, selectedMonth, finalDay);
    if (finalDate >= computedMinDate && finalDate <= computedMaxDate) {
      onDone(finalDate);
    }
  };

  // --- WheelPicker-based rendering ---
  // Filter years to only those with at least one valid month/day in range
  const validYears = years.filter((year) => {
    for (let m = 0; m < 12; m++) {
      const daysInMonth = getDaysInMonth(year, m);
      for (let d = 1; d <= daysInMonth; d++) {
        const dt = new Date(year, m, d);
        if (dt >= computedMinDate && dt <= computedMaxDate) return true;
      }
    }
    return false;
  });
  const yearOptions: WheelPickerOption[] = validYears.map((y) => ({
    label: y.toString(),
    value: y.toString(),
  }));

  // Filter months for selectedYear to only those with at least one valid day in range
  const validMonths = MONTHS.map((label, i) => {
    const daysInMonth = getDaysInMonth(selectedYear, i);
    for (let d = 1; d <= daysInMonth; d++) {
      const dt = new Date(selectedYear, i, d);
      if (dt >= computedMinDate && dt <= computedMaxDate)
        return { label, value: i.toString() };
    }
    return null;
  }).filter(Boolean) as WheelPickerOption[];
  // If only one valid month, show just that month (no duplicates in the wheel)
  const monthOptions: WheelPickerOption[] =
    validMonths.length === 1 ? [validMonths[0]] : validMonths;

  // Filter days for selectedYear+selectedMonth to only those in range
  const validDays = Array.from(
    { length: getDaysInMonth(selectedYear, selectedMonth) },
    (_, i) => i + 1
  ).filter((d) => {
    const dt = new Date(selectedYear, selectedMonth, d);
    return dt >= computedMinDate && dt <= computedMaxDate;
  });
  // If only one valid day, show just that day (no duplicates in the wheel)
  const dayOptions: WheelPickerOption[] =
    validDays.length === 1
      ? [{ label: validDays[0].toString(), value: validDays[0].toString() }]
      : validDays.map((d) => ({ label: d.toString(), value: d.toString() }));

  // If selectedYear/Month/Day are not valid, auto-select the first valid value
  React.useEffect(() => {
    let changed = false;
    if (!validYears.includes(selectedYear) && validYears.length > 0) {
      setSelectedYear(validYears[0]);
      changed = true;
    }
    if (
      !validMonths.some((m) => Number(m.value) === selectedMonth) &&
      validMonths.length > 0
    ) {
      setSelectedMonth(Number(validMonths[0].value));
      changed = true;
    }
    if (!validDays.includes(selectedDay) && validDays.length > 0) {
      setSelectedDay(validDays[0]);
      changed = true;
    }
    // If any value changed, don't run the rest of the logic until next render
  }, [
    selectedYear,
    validYears,
    selectedMonth,
    validMonths,
    selectedDay,
    validDays,
  ]);

  return (
    <FooterDrawer
      open={open}
      onClose={onClose}
      title={title}
      showClose={false}
      height="313px"
    >
      {/* Custom Done Button in header position */}
      <MButton
        className={styles.doneButton}
        size="md"
        variant="link"
        onClick={handleDone}
      >
        Done
      </MButton>
      <div
        className={cn(
          styles.pickerWrap,
          showYearOnly && styles.pickerWrapYearOnly,
          hideDate && styles.pickerWrapHideDate
        )}
      >
        {showYearOnly ? (
          <WheelPickerWrapper>
            <WheelPicker
              options={yearOptions}
              value={selectedYear.toString()}
              onValueChange={(val) => setSelectedYear(Number(val))}
            />
          </WheelPickerWrapper>
        ) : (
          <div style={{ display: "flex", width: "100%" }}>
            <div style={{ flex: 1 }}>
              <WheelPickerWrapper>
                <WheelPicker
                  options={monthOptions}
                  value={selectedMonth.toString()}
                  onValueChange={(val) => handleMonthChange(Number(val))}
                />
              </WheelPickerWrapper>
            </div>
            {!hideDate && (
              <div style={{ flex: 1 }}>
                <WheelPickerWrapper>
                  <WheelPicker
                    options={dayOptions}
                    value={selectedDay.toString()}
                    onValueChange={(val) => setSelectedDay(Number(val))}
                  />
                </WheelPickerWrapper>
              </div>
            )}
            <div style={{ flex: 1 }}>
              <WheelPickerWrapper>
                <WheelPicker
                  options={yearOptions}
                  value={selectedYear.toString()}
                  onValueChange={(val) => setSelectedYear(Number(val))}
                />
              </WheelPickerWrapper>
            </div>
          </div>
        )}
      </div>
    </FooterDrawer>
  );
};

export default MDatePicker;
